import type { Manual, ManualState } from "@arc/contracts";

/**
 * The "More filters" advanced-filter state (mockup #16) — everything the dialog controls beyond the toolbar chips.
 * Client-side only: applied over the already-loaded manuals list. "Assigned to me" needs one ids-only read
 * ({@link useMyManualAssignments}); "Only linked" is the list item's own `parentManualId`.
 */
export interface ManualFilters {
  /** An exact creator username (chosen from the facility's own creators — {@link facilityCreators}), or null = any. */
  creator: string | null;
  /** Selected lifecycle states; empty = all. */
  statuses: ManualState[];
  /** Only manuals whose creator is the signed-in user. */
  createdByMe: boolean;
  /** Only manuals the signed-in user is an ASSIGNED REVIEWER of (ADR-019). */
  assignedToMe: boolean;
  /** Only LINKED copies (ADR-020) — manuals with a `parentManualId`. */
  onlyLinked: boolean;
}

export const EMPTY_MANUAL_FILTERS: ManualFilters = { creator: null, statuses: [], createdByMe: false, assignedToMe: false, onlyLinked: false };

/** What the predicate needs to know about the signed-in user. */
export interface ManualFilterContext {
  myUsername: string | null;
  /** The ids of the manuals the user is assigned to — `useMyManualAssignments`, as a Set. `null` = NOT KNOWN YET
   *  (loading, or the read failed): the "assigned to me" predicate is then skipped rather than rejecting everything,
   *  and the view says so beside the list (diff-panel). */
  assignedIds: ReadonlySet<number> | null;
}

/** True when any advanced filter is set (drives the toolbar's "filters active" dot + whether Reset does anything). */
export function manualFiltersActive(f: ManualFilters): boolean {
  return f.creator !== null || f.statuses.length > 0 || f.createdByMe || f.assignedToMe || f.onlyLinked;
}

/** Does a manual pass the advanced filters? */
export function matchesManualFilters(m: Manual, f: ManualFilters, ctx: ManualFilterContext): boolean {
  if (f.creator !== null && m.creatorUsername !== f.creator) return false;
  if (f.statuses.length > 0 && !f.statuses.includes(m.state)) return false;
  if (f.createdByMe && (ctx.myUsername === null || m.creatorUsername !== ctx.myUsername)) return false;
  if (f.assignedToMe && ctx.assignedIds != null && !ctx.assignedIds.has(m.id)) return false;
  if (f.onlyLinked && m.parentManualId === null) return false;
  return true;
}

/**
 * The distinct creators of a facility's manuals — the ONLY users the Creator combobox offers (mockup #16 scoped to
 * "users involved on this facility", not the whole realm). Derived from the already-loaded lists (each manual carries
 * `creatorUsername`), so it needs no endpoint; a manual with an unknown creator (null — ~82% of migrated rows) simply
 * contributes no option. Sorted, de-duped, case-insensitive.
 */
export function facilityCreators(...lists: readonly Manual[][]): string[] {
  const set = new Set<string>();
  for (const list of lists) for (const m of list) if (m.creatorUsername) set.add(m.creatorUsername);
  return [...set].sort((a, b) => a.localeCompare(b));
}
